import {
  BASEBALL_TRADING_COMPETITION_CODE,
  FOOTBALL_TRADING_COMPETITION_CODE,
  HOCKEY_TRADING_COMPETITION_CODE,
  canonicalCompetitionCode,
} from './competitionIdentity';

export type PlayoffPictureCategory = 'DIVISION' | 'WILD_CARD' | 'OUTSIDE';

export interface PlayoffPictureTeam {
  team_code: string;
  team_name?: string | null;
  team_short_name?: string | null;
  conference_code: string | null;
  division_code: string | null;
  team_state: string;
  qualification_status: 'PENDING' | 'QUALIFIED' | 'NOT_QUALIFIED';
  season_phase: string;
  standings_snapshot_id: number | null;
  playoff_picture_category: PlayoffPictureCategory | null;
  playoff_picture_rank: number | null;
  playoff_picture_order: number | null;
}

export interface PlayoffPictureSection<T> {
  key: string;
  topLevelCode: string;
  divisionCode?: string;
  kind: PlayoffPictureCategory;
  data: T[];
  showCutLineAfter: boolean;
}

export type StandingsMetricPill = {
  label: 'PCT' | 'PTS' | 'WCGB' | 'SEED';
  displayValue: string;
};

export interface StandingsMetricSource {
  season_phase: string;
  standings_win_percentage_display: string | null;
  standings_points: number | null;
  playoff_seed: number | null;
  wild_card_games_behind_display: string | null;
}

export type PlayoffPictureStatusFilter = 'ALL' | 'ACTIVE' | 'ELIMINATED';

export type PlayoffPictureSectionOptions = {
  competitionCode: string | null | undefined;
  searchQuery?: string;
  statusFilter?: PlayoffPictureStatusFilter;
};

const CATEGORIES: readonly PlayoffPictureCategory[] = [
  'DIVISION',
  'WILD_CARD',
  'OUTSIDE',
];

const SUPPORTED_COMPETITIONS = new Set([
  BASEBALL_TRADING_COMPETITION_CODE,
  HOCKEY_TRADING_COMPETITION_CODE,
  FOOTBALL_TRADING_COMPETITION_CODE,
]);

const TOP_LEVEL_CODES_BY_COMPETITION: Readonly<Record<string, ReadonlySet<string>>> = {
  [BASEBALL_TRADING_COMPETITION_CODE]: new Set(['AL', 'NL']),
  [HOCKEY_TRADING_COMPETITION_CODE]: new Set(['EAST', 'WEST']),
  [FOOTBALL_TRADING_COMPETITION_CODE]: new Set(['AFC', 'NFC']),
};

const NHL_DIVISION_CODES_BY_CONFERENCE: Readonly<Record<string, ReadonlySet<string>>> = {
  EAST: new Set(['ATLANTIC', 'METROPOLITAN']),
  WEST: new Set(['CENTRAL', 'PACIFIC']),
};

function normalizedCode(value: string | null | undefined): string {
  return (value ?? '').trim().toUpperCase();
}

function isPositiveSafeInteger(value: number | null): value is number {
  return Number.isSafeInteger(value) && (value ?? 0) > 0;
}

function isEliminated(
  team: Pick<PlayoffPictureTeam, 'qualification_status' | 'team_state'>,
): boolean {
  return (
    team.qualification_status === 'NOT_QUALIFIED' ||
    team.team_state.trim().toUpperCase() === 'ELIMINATED'
  );
}

function matchesStatus(
  team: PlayoffPictureTeam,
  statusFilter: PlayoffPictureStatusFilter,
): boolean {
  if (statusFilter === 'ALL') return true;
  if (statusFilter === 'ELIMINATED') return isEliminated(team);

  const teamState = team.team_state.trim().toUpperCase();
  return !isEliminated(team) && (teamState === 'ACTIVE' || teamState === 'PLAYING');
}

function matchesSearch(team: PlayoffPictureTeam, normalizedQuery: string): boolean {
  if (!normalizedQuery) return true;

  return [team.team_code, team.team_name, team.team_short_name]
    .filter((value): value is string => typeof value === 'string')
    .some((value) => value.toLocaleUpperCase().includes(normalizedQuery));
}

function hasCompleteSnapshot<T extends PlayoffPictureTeam>(
  teams: readonly T[],
  canonicalCompetition: string,
): boolean {
  if (teams.length === 0) return false;

  const snapshotId = teams[0]?.standings_snapshot_id;
  if (!isPositiveSafeInteger(snapshotId)) return false;

  const supportedTopLevelCodes = TOP_LEVEL_CODES_BY_COMPETITION[canonicalCompetition];
  if (!supportedTopLevelCodes) return false;

  const teamCodes = new Set<string>();
  const providerOrders = new Set<number>();
  const ranksByScope = new Map<string, Set<number>>();
  for (const team of teams) {
    const teamCode = normalizedCode(team.team_code);
    const topLevelCode = normalizedCode(team.conference_code);
    const category = team.playoff_picture_category;
    const rank = team.playoff_picture_rank;
    const providerOrder = team.playoff_picture_order;
    if (
      team.season_phase !== 'REGULAR_SEASON' ||
      team.standings_snapshot_id !== snapshotId ||
      teamCode === '' ||
      teamCodes.has(teamCode) ||
      !supportedTopLevelCodes.has(topLevelCode) ||
      !CATEGORIES.includes(category as PlayoffPictureCategory) ||
      !isPositiveSafeInteger(rank) ||
      !isPositiveSafeInteger(providerOrder) ||
      providerOrders.has(providerOrder)
    ) {
      return false;
    }

    if (canonicalCompetition === HOCKEY_TRADING_COMPETITION_CODE) {
      const divisionCode = normalizedCode(team.division_code);
      if (!NHL_DIVISION_CODES_BY_CONFERENCE[topLevelCode]?.has(divisionCode)) {
        return false;
      }
    }

    const rankScope = `${topLevelCode}:${category}`;
    const ranks = ranksByScope.get(rankScope) ?? new Set<number>();
    if (ranks.has(rank)) return false;

    teamCodes.add(teamCode);
    providerOrders.add(providerOrder);
    ranks.add(rank);
    ranksByScope.set(rankScope, ranks);
  }

  for (let expectedOrder = 1; expectedOrder <= teams.length; expectedOrder += 1) {
    if (!providerOrders.has(expectedOrder)) return false;
  }
  for (const ranks of ranksByScope.values()) {
    for (let expectedRank = 1; expectedRank <= ranks.size; expectedRank += 1) {
      if (!ranks.has(expectedRank)) return false;
    }
  }

  return true;
}

function categorySection<T extends PlayoffPictureTeam>(
  topLevelCode: string,
  kind: PlayoffPictureCategory,
  data: T[],
  hasOutside: boolean,
): PlayoffPictureSection<T> {
  return {
    key: `${topLevelCode}:${kind}`,
    topLevelCode,
    kind,
    data,
    showCutLineAfter: kind === 'WILD_CARD' && data.length > 0 && hasOutside,
  };
}

function buildConferenceSections<T extends PlayoffPictureTeam>(
  topLevelCode: string,
  teams: T[],
): PlayoffPictureSection<T>[] {
  const hasOutside = teams.some(({ playoff_picture_category }) => (
    playoff_picture_category === 'OUTSIDE'
  ));

  return CATEGORIES
    .map((kind) => categorySection(
      topLevelCode,
      kind,
      teams.filter(({ playoff_picture_category }) => playoff_picture_category === kind),
      hasOutside,
    ))
    .filter(({ data }) => data.length > 0);
}

function buildHockeyConferenceSections<T extends PlayoffPictureTeam>(
  topLevelCode: string,
  teams: T[],
): PlayoffPictureSection<T>[] {
  const divisionTeams = teams.filter(
    ({ playoff_picture_category }) => playoff_picture_category === 'DIVISION',
  );
  const teamsByDivision = new Map<string, T[]>();

  for (const team of divisionTeams) {
    const divisionCode = normalizedCode(team.division_code);
    const existing = teamsByDivision.get(divisionCode) ?? [];
    existing.push(team);
    teamsByDivision.set(divisionCode, existing);
  }

  const divisionSections = [...teamsByDivision].map(
    ([divisionCode, data]): PlayoffPictureSection<T> => ({
      key: `${topLevelCode}:DIVISION:${divisionCode}`,
      topLevelCode,
      divisionCode,
      kind: 'DIVISION',
      data,
      showCutLineAfter: false,
    }),
  );
  const wildCardTeams = teams.filter(
    ({ playoff_picture_category }) => playoff_picture_category === 'WILD_CARD',
  );
  const outsideTeams = teams.filter(
    ({ playoff_picture_category }) => playoff_picture_category === 'OUTSIDE',
  );

  return [
    ...divisionSections,
    ...(wildCardTeams.length > 0
      ? [categorySection(topLevelCode, 'WILD_CARD', wildCardTeams, outsideTeams.length > 0)]
      : []),
    ...(outsideTeams.length > 0
      ? [categorySection(topLevelCode, 'OUTSIDE', outsideTeams, false)]
      : []),
  ];
}

export function buildPlayoffPictureSections<T extends PlayoffPictureTeam>(
  sourceTeams: readonly T[],
  {
    competitionCode,
    searchQuery = '',
    statusFilter = 'ALL',
  }: PlayoffPictureSectionOptions,
): PlayoffPictureSection<T>[] | null {
  const canonicalCode = canonicalCompetitionCode(competitionCode);
  if (
    !SUPPORTED_COMPETITIONS.has(canonicalCode) ||
    !hasCompleteSnapshot(sourceTeams, canonicalCode)
  ) {
    return null;
  }

  const normalizedQuery = searchQuery.trim().toLocaleUpperCase();
  const filteredTeams = sourceTeams
    .filter((team) => (
      matchesStatus(team, statusFilter) && matchesSearch(team, normalizedQuery)
    ))
    .slice()
    .sort((left, right) => (
      (left.playoff_picture_order as number) - (right.playoff_picture_order as number)
    ));
  const teamsByTopLevel = new Map<string, T[]>();

  for (const team of filteredTeams) {
    const topLevelCode = normalizedCode(team.conference_code);
    const existing = teamsByTopLevel.get(topLevelCode) ?? [];
    existing.push(team);
    teamsByTopLevel.set(topLevelCode, existing);
  }

  return [...teamsByTopLevel].flatMap(([topLevelCode, teams]) => (
    canonicalCode === HOCKEY_TRADING_COMPETITION_CODE
      ? buildHockeyConferenceSections(topLevelCode, teams)
      : buildConferenceSections(topLevelCode, teams)
  ));
}

function nonblankDisplayValue(value: string | null): string | null {
  return value !== null && value.trim() !== '' ? value : null;
}

export function buildStandingsMetricPills(
  competitionCode: string | null | undefined,
  source: StandingsMetricSource | null | undefined,
): StandingsMetricPill[] {
  if (!source || source.season_phase !== 'REGULAR_SEASON') return [];

  const canonicalCode = canonicalCompetitionCode(competitionCode);
  if (canonicalCode === BASEBALL_TRADING_COMPETITION_CODE) {
    const percentage = nonblankDisplayValue(source.standings_win_percentage_display);
    const wildCardGamesBehind = nonblankDisplayValue(
      source.wild_card_games_behind_display,
    );
    return [
      ...(percentage ? [{ label: 'PCT' as const, displayValue: percentage }] : []),
      ...(wildCardGamesBehind
        ? [{ label: 'WCGB' as const, displayValue: wildCardGamesBehind }]
        : []),
    ];
  }

  if (canonicalCode === HOCKEY_TRADING_COMPETITION_CODE) {
    return Number.isInteger(source.standings_points) && (source.standings_points ?? -1) >= 0
      ? [{ label: 'PTS', displayValue: String(source.standings_points) }]
      : [];
  }

  if (canonicalCode === FOOTBALL_TRADING_COMPETITION_CODE) {
    const percentage = nonblankDisplayValue(source.standings_win_percentage_display);
    const seed = source.playoff_seed;
    return [
      ...(percentage ? [{ label: 'PCT' as const, displayValue: percentage }] : []),
      ...(Number.isInteger(seed) && (seed ?? 0) > 0
        ? [{ label: 'SEED' as const, displayValue: String(seed) }]
        : []),
    ];
  }

  return [];
}
